import type { ValidationErrors, ValidationResult } from '$lib/forms';

import type { ReadonlyMachineConfig } from './robot-config/machine-config';

const LENGTH_ERROR = 'Must contain a letter or number';
const START_CHAR_ERROR = 'Cannot start with a dash or underscore';
const INVALID_CHAR_ERROR =
  'Can only contain letters, numbers, dashes, and underscores';
const MACHINE_OR_PART_START_CHAR_ERROR = 'Cannot start with a dash';
const MACHINE_OR_PART_INVALID_CHAR_ERROR =
  'Can only contain letters, numbers, and dashes';

const getCharLimitError = (limit: number) =>
  `Cannot contain more than ${limit} characters`;
const getNotUniqueNameError = (itemType: string) =>
  `Name is already used in another ${itemType}`;

const RESOURCE_OPTIONS = {
  itemType: 'resource',
  validRegEx: /^[a-z0-9][\w-]{1,59}$/iu,
  validCharacterRegEx: /^[\w-]*$/iu,
  maxLength: 60,
  startCharError: START_CHAR_ERROR,
  invalidCharError: INVALID_CHAR_ERROR,
} as const;

const REGISTRY_ITEM_OPTIONS = {
  itemType: 'registry item',
  validRegEx: /^[a-z0-9][\w-]{1,99}$/iu,
  validCharacterRegEx: /^[\w-]*$/iu,
  maxLength: 100,
  startCharError: START_CHAR_ERROR,
  invalidCharError: INVALID_CHAR_ERROR,
} as const;

const FRAGMENT_PREFIX_OPTIONS = {
  itemType: 'fragment-prefix',
  validRegEx: /^[a-z0-9][\w-]{0,59}$/iu,
  validCharacterRegEx: /^[\w-]*$/iu,
  maxLength: 60,
  startCharError: START_CHAR_ERROR,
  invalidCharError: INVALID_CHAR_ERROR,
} as const;

const MACHINE_OR_PART_OPTIONS = {
  validRegEx: /^[a-z0-9][-]{1,59}$/iu,
  validCharacterRegEx: /^[a-z0-9-]*$/iu,
  maxLength: 60,
  startCharError: MACHINE_OR_PART_START_CHAR_ERROR,
  invalidCharError: MACHINE_OR_PART_INVALID_CHAR_ERROR,
} as const;

const MACHINE_OPTIONS = {
  ...MACHINE_OR_PART_OPTIONS,
  itemType: 'machine',
} as const;

const PART_OPTIONS = {
  ...MACHINE_OR_PART_OPTIONS,
  itemType: 'part',
} as const;

const FRAGMENT_OPTIONS = {
  ...MACHINE_OR_PART_OPTIONS,
  itemType: 'fragment',
} as const;

const LOCATION_OPTIONS = {
  itemType: 'location',
  validRegEx: /^(?=.*[^-_\s]+.*).{1,49}$/iu,
  validCharacterRegEx: /./iu,
  maxLength: 50,
  startCharError: START_CHAR_ERROR,
  invalidCharError: '',
} as const;

const TELEOP_WORKSPACE_OPTIONS = {
  itemType: 'teleop workspace',
  validRegEx: /^.{1,60}$/iu,
  validCharacterRegEx: /./iu,
  maxLength: 60,
  invalidCharError: '',
} as const;

const DASHBOARD_OPTIONS = {
  itemType: 'dashboard',
  validRegEx: /^.{1,60}$/iu,
  validCharacterRegEx: /./iu,
  maxLength: 60,
  invalidCharError: '',
} as const;

const NAMESPACE_OPTIONS = {
  itemType: 'namespace',
  validRegEx: /^[a-z0-9-]*$/u,
  validCharacterRegEx: /^[a-z0-9-]*$/iu,
  maxLength: 60,
  startCharError: START_CHAR_ERROR,
  invalidCharError: MACHINE_OR_PART_INVALID_CHAR_ERROR,
} as const;

interface ValidationOptions {
  itemType: string;
  validRegEx: RegExp;
  validCharacterRegEx: RegExp;
  maxLength: number;
  startCharError?: string;
  invalidCharError: string;
}

export interface ItemName {
  name: string;
}

export const validateResourceName = (
  name: ItemName,
  names: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateName(name, names, RESOURCE_OPTIONS);
};

export const validateResourceRename = (
  currentName: string,
  next: ItemName,
  names: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateRename(currentName, next, names, RESOURCE_OPTIONS);
};

export const validateRegistryItemName = (
  name: ItemName,
  names: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateName(name, names, REGISTRY_ITEM_OPTIONS);
};

export const validatePartRename = (
  currentName: string,
  next: ItemName,
  existingNames: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateRename(currentName, next, existingNames, PART_OPTIONS);
};

export const validateFragmentName = (
  name: ItemName,
  existingNames: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateName(name, existingNames, FRAGMENT_OPTIONS);
};

export const validateFragmentRename = (
  currentName: string,
  next: ItemName,
  existingNames: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateRename(currentName, next, existingNames, FRAGMENT_OPTIONS);
};

export const validateMachineName = (
  name: ItemName,
  existingNames: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateName(name, existingNames, MACHINE_OPTIONS);
};

export const validateMachineRename = (
  currentName: string,
  next: ItemName,
  existingNames: ReadonlySet<string>
): ValidationResult<ItemName> => {
  return validateRename(currentName, next, existingNames, MACHINE_OPTIONS);
};

export const validateLocationRename = (
  currentName: string,
  next: ItemName
): ValidationResult<ItemName> => {
  return validateRename(currentName, next, new Set<string>(), LOCATION_OPTIONS);
};

export const validateTeleopWorkspaceRename = (
  currentName: string,
  next: ItemName
): ValidationResult<ItemName> => {
  return validateRename(
    currentName,
    next,
    new Set<string>(),
    TELEOP_WORKSPACE_OPTIONS
  );
};

export const validateDashboardRename = (
  currentName: string,
  next: ItemName
): ValidationResult<ItemName> => {
  return validateRename(
    currentName,
    next,
    new Set<string>(),
    DASHBOARD_OPTIONS
  );
};

export const validateNamespace = (
  currentName: string,
  next: ItemName
): ValidationResult<ItemName> => {
  return validateRename(
    currentName,
    next,
    new Set<string>(),
    NAMESPACE_OPTIONS
  );
};

const validateName = (
  name: ItemName,
  existingNames: ReadonlySet<string>,
  options: ValidationOptions
): ValidationResult<ItemName> => {
  let errors: ValidationErrors<ItemName> | undefined;
  const values = { name: name.name.trim() };

  const {
    itemType,
    validRegEx,
    validCharacterRegEx,
    maxLength,
    startCharError,
    invalidCharError,
  } = options;
  if (values.name.length === 0) {
    return { values, errors: { name: LENGTH_ERROR } };
  }
  if (!isNameUnique(values, existingNames, itemType)) {
    return { values, errors: { name: getNotUniqueNameError(itemType) } };
  }
  if (validRegEx.test(values.name)) {
    return { values };
  }

  if (
    startCharError !== undefined &&
    (values.name.startsWith('-') || values.name.startsWith('_'))
  ) {
    errors = { name: startCharError };
  }
  if (values.name.length > maxLength) {
    const prevError = errors?.name ? `${errors.name}; ` : '';
    errors = { name: `${prevError}${getCharLimitError(maxLength)}` };
  }
  if (!validCharacterRegEx.test(values.name)) {
    const prevError = errors?.name ? `${errors.name}; ` : '';
    errors = { name: `${prevError}${invalidCharError}` };
  }

  return { values, errors };
};

const validateRename = (
  currentName: string,
  next: ItemName,
  existingNames: ReadonlySet<string>,
  options: ValidationOptions
): ValidationResult<ItemName> => {
  const values = { name: next.name.trim() };

  if (currentName === values.name) {
    return { values };
  }

  return validateName(next, existingNames, options);
};

const isNameUnique = (
  values: ItemName,
  names: ReadonlySet<string>,
  itemType: string
): boolean => {
  if (itemType === 'machine') {
    const sanitizedName = values.name.toLowerCase();
    const sanitizedNames = new Set(
      [...names].map((name) => name.toLowerCase())
    );
    return !sanitizedNames.has(sanitizedName);
  }
  return !names.has(values.name);
};

/** Replaces spaces with hyphens. */
export const hyphenateWhitespace = (value: string): string =>
  value.replaceAll(' ', '-');

export const getPartNameList = (
  config: ReadonlyMachineConfig
): ReadonlySet<string> => {
  return new Set(Object.values(config.parts).map(({ name }) => name));
};

export const validateLocationName = (
  next: ItemName
): ValidationResult<ItemName> => {
  let errors: ValidationErrors<ItemName> | undefined;
  const values = { name: next.name.trim() };

  if (values.name.length === 0) {
    return { values, errors: { name: LENGTH_ERROR } };
  }

  if (values.name.length > LOCATION_OPTIONS.maxLength) {
    errors = { name: getCharLimitError(LOCATION_OPTIONS.maxLength) };
  }

  if (values.name.startsWith('-') || values.name.startsWith('_')) {
    const prevError = errors?.name ? `${errors.name}; ` : '';
    errors = { name: `${prevError}${LOCATION_OPTIONS.startCharError}` };
  }

  if (!LOCATION_OPTIONS.validRegEx.test(values.name)) {
    const prevError = errors?.name ? `${errors.name}; ` : '';
    errors = { name: `${prevError}${LOCATION_OPTIONS.invalidCharError}` };
  }

  return { values, errors };
};

export const validateFragmentPrefixNameFormat = (
  name: string
): string | undefined => {
  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return LENGTH_ERROR;
  }

  if (trimmedName.startsWith('-') || trimmedName.startsWith('_')) {
    return START_CHAR_ERROR;
  }

  if (trimmedName.length > FRAGMENT_PREFIX_OPTIONS.maxLength) {
    return getCharLimitError(FRAGMENT_PREFIX_OPTIONS.maxLength);
  }

  if (!FRAGMENT_PREFIX_OPTIONS.validCharacterRegEx.test(trimmedName)) {
    return INVALID_CHAR_ERROR;
  }

  if (!FRAGMENT_PREFIX_OPTIONS.validRegEx.test(trimmedName)) {
    return 'Must be a valid fragment prefix, starting with at least one letter or number';
  }

  return undefined;
};
